import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import DeviceHubRoundedIcon from "@mui/icons-material/DeviceHubRounded";
import WaterDropRoundedIcon from "@mui/icons-material/WaterDropRounded";
import BatteryChargingFullRoundedIcon from "@mui/icons-material/BatteryChargingFullRounded";
import MonitorHeartRoundedIcon from "@mui/icons-material/MonitorHeartRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import SensorsRoundedIcon from "@mui/icons-material/SensorsRounded";

function getStatusStyle(status) {
  switch (status) {
    case "LOW_FLOW":
      return {
        bg: "#fff7ed",
        chipBg: "#ffedd5",
        border: "#fdba74",
        text: "#c2410c",
      };
    case "HIGH_FLOW":
      return {
        bg: "#fef2f2",
        chipBg: "#fee2e2",
        border: "#fca5a5",
        text: "#b91c1c",
      };
    case "NORMAL":
      return {
        bg: "#ecfdf3",
        chipBg: "#d1fadf",
        border: "#86efac",
        text: "#15803d",
      };
    default:
      return {
        bg: "#f8fafc",
        chipBg: "#e2e8f0",
        border: "#cbd5e1",
        text: "#334155",
      };
  }
}

function formatTime(dateValue) {
  if (!dateValue) return "--";
  return new Date(dateValue).toLocaleString();
}

function InfoMetric({ title, value, unit, iconBoxBg, icon }) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid #e2e8f0",
        boxShadow: "0 6px 18px rgba(15, 23, 42, 0.05)",
        height: "100%",
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Typography sx={{ color: "#64748b", fontWeight: 700, fontSize: 14 }}>
            {title}
          </Typography>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              bgcolor: iconBoxBg,
              display: "grid",
              placeItems: "center",
            }}
          >
            {icon}
          </Box>
        </Stack>

        <Typography sx={{ fontSize: 30, fontWeight: 800, color: "#0f172a", lineHeight: 1.1 }}>
          {value ?? "--"}
        </Typography>
        <Typography sx={{ mt: 0.5, color: "#64748b", fontSize: 14 }}>
          {unit}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function TelemetryCard({ item }) {
  const statusStyle = getStatusStyle(item.status);
  const battery = Number(item.battery || 0);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px solid ${statusStyle.border}`,
        overflow: "hidden",
        boxShadow: "0 14px 40px rgba(2, 6, 23, 0.08)",
        background: "#fff",
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          background: statusStyle.bg,
          borderBottom: "1px solid #edf2f7",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={1.5}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 3,
                bgcolor: "#0f2f35",
                color: "#fff",
                display: "grid",
                placeItems: "center",
              }}
            >
              <DeviceHubRoundedIcon />
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 20, color: "#0f172a" }}>
                {item.device_id || "Unknown Device"}
              </Typography>
              <Typography sx={{ color: "#64748b", fontSize: 13 }}>
                {item.topic || "--"}
              </Typography>
            </Box>
          </Stack>

          <Chip
            label={item.status || "UNKNOWN"}
            sx={{
              fontWeight: 800,
              color: statusStyle.text,
              backgroundColor: statusStyle.chipBg,
              border: `1px solid ${statusStyle.border}`,
            }}
          />
        </Stack>
      </Box>

      <CardContent sx={{ p: 2.5 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <InfoMetric
              title="Flow In"
              value={item.flow_in}
              unit="drops/min"
              iconBoxBg="#e0f2fe"
              icon={<WaterDropRoundedIcon sx={{ color: "#0369a1" }} />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <InfoMetric
              title="Flow Out"
              value={item.flow_out}
              unit="ml/hr"
              iconBoxBg="#dcfce7"
              icon={<MonitorHeartRoundedIcon sx={{ color: "#15803d" }} />}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={4}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid #e2e8f0",
                boxShadow: "0 6px 18px rgba(15, 23, 42, 0.05)",
                height: "100%",
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Typography sx={{ color: "#64748b", fontWeight: 700, fontSize: 14 }}>
                    Battery
                  </Typography>
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: 2,
                      bgcolor: "#fff7ed",
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <BatteryChargingFullRoundedIcon sx={{ color: "#c2410c" }} />
                  </Box>
                </Stack>

                <Typography sx={{ fontSize: 30, fontWeight: 800, color: "#0f172a", mb: 1 }}>
                  {battery}%
                </Typography>

                <LinearProgress
                  variant="determinate"
                  value={Math.max(0, Math.min(100, battery))}
                  sx={{
                    height: 10,
                    borderRadius: 999,
                    bgcolor: "#e2e8f0",
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2.5 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: "1px solid #e2e8f0",
                bgcolor: "#f8fafc",
              }}
            >
              <Stack direction="row" spacing={1.2} alignItems="center" mb={1}>
                <AccessTimeRoundedIcon sx={{ color: "#64748b" }} />
                <Typography sx={{ fontWeight: 700, color: "#334155" }}>
                  Last Updated
                </Typography>
              </Stack>
              <Typography sx={{ color: "#0f172a", fontWeight: 700 }}>
                {formatTime(item.created_at)}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: "1px solid #e2e8f0",
                bgcolor: "#f8fafc",
              }}
            >
              <Stack direction="row" spacing={1.2} alignItems="center" mb={1}>
                <SensorsRoundedIcon sx={{ color: "#64748b" }} />
                <Typography sx={{ fontWeight: 700, color: "#334155" }}>
                  Monitoring State
                </Typography>
              </Stack>
              <Typography sx={{ color: statusStyle.text, fontWeight: 800 }}>
                {item.status || "UNKNOWN"}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}